<script lang="ts" setup>
import { AxiosError } from "axios";
import { computed, onBeforeMount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { applicationService } from "@/app/services/ApplicationService";
import { messageService } from "@/app/services/MessageService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppButtonOutline from "@/components/atoms/button/AppButtonOutline.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import TextAreaGroup from "@/components/molecules/form/TextAreaGroup.vue";
import Header from "@/components/organisms/common/Header.vue";
import { authStore } from "@/store/auth";
import { Application } from "@/types/order/Application";
import CustomValidationObserver from "@/plugins/CustomValidationObserver.vue";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { useCustomRouter } from "@/utils/router";

const errorModal = useErrorModal();
const loader = useLoader();
const router = useRouter();
const route = useRoute();
const customRouter = useCustomRouter();

const form = ref<CustomValidationObserver | null>(null);
const message = ref("応募させていただきました。よろしくお願いします。");
const application = ref<Application | null>(null);

onBeforeMount(async () => {
  // パス不正の場合は仕事一覧画面に遷移
  const applicationId = Number(route.params.applicationId);
  if (Number.isNaN(applicationId)) {
    await customRouter.replaceIgnoreError({ name: "Search" });
    return;
  }

  try {
    loader.show();
    application.value = await applicationService.fetchApplication(
      applicationId
    );
  } catch (e) {
    // 見つからなかった場合は仕事一覧画面に遷移
    if (e && (e as AxiosError).isAxiosError) {
      if ((e as AxiosError).response!.status === 404) {
        await customRouter.replaceIgnoreError({ name: "Search" });
        return;
      }
    }
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }
});

const productAssetSuffix = computed(() => authStore.productAssetSuffix);
const mascotImage = computed(() =>
  require("@/assets/svg/caikuma_send_message" +
    authStore.productAssetSuffix +
    ".svg")
);
const formValues = computed(() => ({ message: message.value }));
const orderRegisterCalendarRoute = computed(() => ({
  name: "OrdersRegisterCalendar",
  params: { applicationId: route.params.applicationId }
}));

onMounted(() => {
  form.value!.resetForm(formValues);
});

function onClickBackDetail() {
  // 案件詳細画面まで戻る（ApplicationCompleted と実装を合わせている）
  router.go(-4);
}
async function sendMessage() {
  try {
    loader.show();
    await messageService.createMessage({
      text: message.value,
      worker: authStore.user!.id,
      office: application.value!.order.office.id
    });
    await customRouter.pushIgnoreError(orderRegisterCalendarRoute.value);
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }
}
</script>

<template>
  <Header>
    <template v-slot:left>
      <FeatherIcon icon="chevron-left" @click="onClickBackDetail" />
    </template>
    メッセージ送信
  </Header>

  <div>
    <div
      class="d-flex align-items-center justify-content-center flex-column mt-2 mb-4"
    >
      <img width="114" :src="mascotImage" />
      <div class="mt-2 text-black text-weight-600 text-center">
        お仕事の確定おめでとうございます！
      </div>
    </div>
    <div
      class="mt-4 mb-3 pt-2 px-3 pb-3"
      :class="`bg-banner${productAssetSuffix}`"
    >
      <div class="text-white text-center">check!</div>
      <div class="mt-1 mb-2 text-black text-weight-600 text-center">
        事務所様へメッセージを送信しましょう
      </div>
      <div class="text-size-12">
        メッセージのやりとりを事前に行っておくことで、事業所様との連絡がスムーズになります。
      </div>
    </div>
    <div class="mx-3">
      <div class="text-size-14 mb-3">
        以下のメッセージを編集して、「送信」を押すとメッセージが送信できます。
      </div>
      <CustomValidationObserver
        ref="form"
        v-slot="{ handleSubmit }"
        :initial-values="formValues"
      >
        <ValidationProvider
          vid="message"
          name="メッセージ"
          rules="required|max:2000"
          v-slot="validationState"
        >
          <TextAreaGroup
            v-model="message"
            rows="3"
            :validation-state="validationState"
          />
        </ValidationProvider>
        <div
          class="d-flex align-items-center justify-content-center flex-column my-4"
        >
          <AppButton class="w-100 mt-2" @click="handleSubmit(sendMessage)"
            >送信</AppButton
          >
          <AppButtonOutline
            class="w-100 mt-2"
            variant="primary"
            :to="orderRegisterCalendarRoute"
            >スキップ</AppButtonOutline
          >
        </div>
      </CustomValidationObserver>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.bg-banner {
  background-color: #fbce5a;
}

.bg-banner-caitech_pharmacist {
  background-color: #6ce0d7;
}
</style>
